import {
  Box,
  Checkbox,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { RestartAlt } from '@mui/icons-material';
import { memo } from 'react';
import propTypes from 'prop-types';
import { useSelector } from 'react-redux';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';

const MySwal = withReactContent(Swal);

const SpecialtyItem = memo((props) => {
  const {
    specialty,
    subSpecialties,
    handleChange,
    disabledItems,
    hasPracticeService,
    handleRestart,
    disabledRestart,
  } = props;
  const specialties = useSelector((state) => state.app.commonSpecialties);
  // define styles
  const isAssociatedPractice = specialty?.isAssociatedPractice === true;
  const tooltipText =
    'Esta especialidad solo puede modificarse desde la configuración de turnos en prácticas por profesional.';
  // redux
  // hooks
  // states
  // effects
  // methods
  const handleMainEntityChangeSwal = async () => {
    return MySwal.fire({
      title: 'Especialidad con subEspecialidades',
      text: '¿Está seguro que desea remover las subEspecialidades?',
      confirmButtonText: 'Sí',
      showConfirmButton: true,
      showCancelButton: true,
      cancelButtonColor: '#d33',
      icon: 'warning',
      customClass: {
        container: 'gap: 100px',
      },
    });
  };
  const handleResetSwal = async () => {
    return MySwal.fire({
      title: 'Se borraran los sinonimos añadidos manualmente',
      text: '¿Desea resetear los sinonimos por defecto?',
      confirmButtonText: 'Sí',
      showConfirmButton: true,
      showCancelButton: true,
      cancelButtonColor: '#d33',
      icon: 'warning',
      customClass: {
        container: 'gap: 100px',
      },
    });
  };
  const handleSubSpecialties = () => {
    for (const subSpecialty of subSpecialties) {
      handleChange({
        value: subSpecialty,
        checked: false,
        noSpecialty: false,
        composedSpecialty: false,
        practices: false,
        isSubSpecialty: false,
        mainSpecialty: '',
        previous: { entityType: 'isSubSpecialty' },
      });
    }
  };

  const handleChangeCheckbox = async (type) => {
    let result;
    if (type === 'specialty') {
      if (subSpecialties.length > 0 && specialty.checked) {
        result = await handleMainEntityChangeSwal();
        if (result.isConfirmed) {
          handleSubSpecialties();
        }
      }
      if (subSpecialties.length === 0 || !specialty.checked || result.isConfirmed)
        handleChange({
          ...specialty,
          checked: !specialty.checked,
          noSpecialty: specialty.checked ? false : !!specialty.noSpecialty,
          composedSpecialty: specialty.checked ? false : !!specialty.composedSpecialty,
          practices: specialty.checked ? false : !!specialty.practices,
          isSubSpecialty: specialty.checked ? false : !!specialty.isSubSpecialty,
          mainSpecialty: specialty.checked ? '' : specialty.mainSpecialty || '',
        });
    } else if (type === 'subSpecialty') {
      if (subSpecialties.length > 0 && !specialty.isSubSpecialty) {
        result = await handleMainEntityChangeSwal();
        if (result.isConfirmed) {
          handleSubSpecialties();
        }
      }
      if (subSpecialties.length === 0 || result.isConfirmed)
        handleChange({
          ...specialty,
          checked: !specialty.isSubSpecialty && !specialty.checked ? true : !!specialty.checked,
          isSubSpecialty: !specialty.isSubSpecialty,
          noSpecialty:
            !specialty.isSubSpecialty && specialty.noSpecialty ? false : !!specialty.noSpecialty,
          practices:
            !specialty.isSubSpecialty && specialty.practices ? false : !!specialty.practices,
          composedSpecialty:
            !specialty.isSubSpecialty && specialty.composedSpecialty
              ? false
              : !!specialty.composedSpecialty,
          mainSpecialty: '',
        });
    } else if (type === 'noSpecialty') {
      if (subSpecialties.length > 0 && !specialty.noSpecialty) {
        result = await handleMainEntityChangeSwal();
        if (result.isConfirmed) {
          handleSubSpecialties();
        }
      }
      if (subSpecialties.length === 0 || result.isConfirmed)
        handleChange({
          ...specialty,
          noSpecialty: !specialty.noSpecialty,
          composedSpecialty:
            !specialty.noSpecialty && specialty.composedSpecialty
              ? false
              : !!specialty.composedSpecialty,
          practices: !specialty.noSpecialty && specialty.practices ? false : !!specialty.practices,
          checked: !specialty.noSpecialty && !specialty.checked ? true : !!specialty.checked,
        });
    } else if (type === 'composedSpecialty') {
      if (subSpecialties.length > 0 && specialty.composedSpecialty) {
        result = await handleMainEntityChangeSwal();
        if (result.isConfirmed) {
          handleSubSpecialties();
        }
      }
      if (subSpecialties.length === 0 || !specialty.composedSpecialty || result.isConfirmed)
        handleChange({
          ...specialty,
          composedSpecialty: !specialty.composedSpecialty,
          noSpecialty:
            !specialty.composedSpecialty && specialty.noSpecialty ? false : !!specialty.noSpecialty,
          practices:
            !specialty.composedSpecialty && specialty.practices ? false : !!specialty.practices,
          checked: !specialty.composedSpecialty && !specialty.checked ? true : !!specialty.checked,
        });
    } else if (type === 'practices') {
      if (subSpecialties.length > 0 && !specialty.practices) {
        result = await handleMainEntityChangeSwal();
        if (result.isConfirmed) {
          handleSubSpecialties();
        }
      }
      if (subSpecialties.length === 0 || result.isConfirmed)
        handleChange({
          ...specialty,
          practices: !specialty.practices,
          composedSpecialty:
            !specialty.practices && specialty.composedSpecialty
              ? false
              : !!specialty.composedSpecialty,
          noSpecialty:
            !specialty.practices && specialty.noSpecialty ? false : !!specialty.noSpecialty,
          checked: !specialty.practices && !specialty.checked ? true : !!specialty.checked,
        });
    }
  };
  const handleResetSynonyms = async ({ specialty }) => {
    const result = await handleResetSwal();
    if (result.isConfirmed) {
      handleRestart({ specialty });
    }
  };

  return (
    <Tooltip
      title={isAssociatedPractice ? tooltipText : ''}
      placement="top"
      arrow
      disableHoverListener={!isAssociatedPractice}
    >
      <Box mb={2} width="100%">
        <Paper
          key={specialty.value}
          elevation={3}
          sx={{
            borderRadius: 3,
            overflow: 'hidden',
            opacity: isAssociatedPractice ? 0.5 : 1,
            pointerEvents: isAssociatedPractice ? 'none' : 'auto',
          }}
        >
          <Box p={2} display="flex" position="relative">
            <Box display="flex" flexDirection="column" justifyContent="center" ml={2} width="100%">
              {/* Nombre de Especialidad con Checkbox */}
              <Box display="flex" alignItems="center">
                <Checkbox
                  checked={!!specialty.checked}
                  onChange={() => handleChangeCheckbox('specialty')}
                  sx={{ color: 'primary.main' }}
                />
                <Typography variant="h8" fontWeight="500">
                  {specialty.value}
                </Typography>
              </Box>
              {/* Nombre en sistema de salud */}
              <Box mb={2} width="90%" ml="2%">
                <FormControl fullWidth>
                  <TextField
                    label="Nombre en sistema de salud (opcional)"
                    disabled={!!disabledItems.find((e) => e.value === specialty.value)}
                    value={specialty.alternativeName || ''}
                    onChange={(event) =>
                      handleChange({ ...specialty, alternativeName: event.target.value })
                    }
                    variant="outlined"
                    size="small"
                  />
                </FormControl>
              </Box>
              {/* Checkbox para Subespecialidades */}
              <Box display="flex" alignItems="center">
                <Checkbox
                  checked={!!specialty.isSubSpecialty}
                  onChange={() => handleChangeCheckbox('subSpecialty')}
                />
                <Typography>Es parte de</Typography>
              </Box>
              {/* Selección de especialidad principal */}
              {specialty.isSubSpecialty && (
                <FormControl sx={{ mb: 1, ml: 1, width: '80%' }} variant="standard" size="small">
                  <InputLabel>Seleccione especialidad</InputLabel>
                  <Select
                    value={specialty.mainSpecialty || ''}
                    onChange={(event) =>
                      handleChange({ ...specialty, mainSpecialty: event.target.value })
                    }
                    label="Seleccione especialidad"
                  >
                    {specialties
                      .filter((e) => !e.noSpecialty && !e.practices && !e.isSubSpecialty)
                      .filter((e) => e.value !== specialty.value)
                      .map((e) => (
                        <MenuItem key={e.value} value={e.value || ''}>
                          {e.value}
                        </MenuItem>
                      ))}
                  </Select>
                </FormControl>
              )}
              {/* Checkbox para No Especialidad */}
              <Box display="flex" alignItems="center">
                <Checkbox
                  checked={!!specialty.noSpecialty}
                  onChange={() => handleChangeCheckbox('noSpecialty')}
                  disabled={specialty.isSubSpecialty}
                />
                <Typography>No se ofrece especialidad</Typography>
              </Box>
              {/* Checkbox para Entidad Compuesta */}
              <Box display="flex" alignItems="center">
                <Checkbox
                  checked={!!specialty.composedSpecialty}
                  onChange={() => handleChangeCheckbox('composedSpecialty')}
                  disabled={specialty.isSubSpecialty}
                />
                <Typography>Es entidad compuesta</Typography>
              </Box>
              {/* Subespecialidades Listadas */}
              {subSpecialties.length > 0 && (
                <Box ml={2}>
                  <Typography variant="body2" color="textSecondary" fontWeight="500">
                    Sub-especialidades
                  </Typography>
                  {subSpecialties.map((e, index) => (
                    <Typography variant="body2" color="textSecondary" key={index}>
                      - {e}
                    </Typography>
                  ))}
                </Box>
              )}
              {/* Checkbox para Prácticas */}
              {hasPracticeService && (
                <Box display="flex" alignItems="center">
                  <Checkbox
                    checked={!!specialty.practices}
                    onChange={(event) => handleChangeCheckbox('practices')}
                    disabled={specialty.isSubSpecialty}
                  />
                  <Typography>Es practica</Typography>
                </Box>
              )}
            </Box>
            {/* Botón de Reinicio */}
            <Box position="absolute" right={16} top={16}>
              <RestartAlt
                onClick={() => !disabledRestart && handleResetSynonyms({ specialty })}
                sx={{
                  color: disabledRestart ? '#ccc' : 'secondary.main',
                  borderRadius: '50%',
                  fontSize: 30,
                  transition: 'all .3s',
                  padding: '4px',
                  '&:hover': {
                    fontSize: !disabledRestart && 34,
                    color: !disabledRestart && 'secondary.dark',
                    cursor: !disabledRestart && 'pointer',
                    background: !disabledRestart && '#f0f0f0',
                  },
                }}
              />
            </Box>
          </Box>
        </Paper>
      </Box>
    </Tooltip>
  );
});

const Props = {
  specialty: propTypes.object,
  subSpecialties: propTypes.array,
  handleChange: propTypes.func,
  disabledItems: propTypes.array,
  hasPracticeService: propTypes.bool,
  handleRestart: propTypes.func,
  disabledRestart: propTypes.bool,
};

SpecialtyItem.propTypes = Props;

export default SpecialtyItem;
